import { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { Card, Form, Button, Spinner, Badge } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { useQuery } from '@apollo/client'
import {
    useChannelConfig,
    ChannelConfigData,
    VicidialCampaignOption,
    fetchVicidialCampaignOptions,
} from './hooks/useChannelConfig'
import { useGetUser } from '../../store/userStore'
import { parseId } from '../../helpers'
import { GET_ALL_ORGANIZATIONS } from '../../gql/queries/organizationsQuery'
import { useAuth } from '../../../modules/auth'
import { STORAGE_NAMES } from '../../helpers/consts'
import { useOrgChannels } from './hooks/useOrgChannels'
import { useChannelValidation } from './hooks/useChannelValidation'
import { ChannelFormModal } from '../assignmentGroups/components/ChannelFormModal'
import { Channel } from '../../types/assignmentGroups'

export const ChannelConfigPage = () => {
    const { auth } = useAuth()
    const userGroups = useGetUser((s) => s.user?.groups)?.map((g: any) => g.name || g) || []
    const currentUserOrgId = useGetUser((s) => (s.user?.user as any)?.organization?.id)
    const isSuperAdmin = userGroups.includes('SuperAdministrador')
    const isAdmin = userGroups.includes('Administrador') || isSuperAdmin

    // Organization selector state
    const [selectedOrgId, setSelectedOrgId] = useState<string | undefined>(undefined)
    const { data: orgData, loading: loadingOrgs } = useQuery(GET_ALL_ORGANIZATIONS, {
        variables: { first: 100 },
        skip: !isSuperAdmin,
    })
    const organizations = orgData?.allOrganizations?.edges?.map((e: any) => e.node) || []

    // Set initial org once loaded
    useEffect(() => {
        if (isSuperAdmin && organizations.length > 0 && !selectedOrgId) {
            // Default to current user's org, or first one
            const defaultOrg = currentUserOrgId || organizations[0]?.id
            setSelectedOrgId(defaultOrg)
        }
    }, [isSuperAdmin, organizations, selectedOrgId, currentUserOrgId])

    // Decode relay ID to numeric ID for the REST API
    const decodedOrgId = selectedOrgId ? String(parseId(selectedOrgId)) : undefined
    const { config, loading, saving, saveOrgConfig } = useChannelConfig(
        isSuperAdmin ? decodedOrgId : undefined
    )
    const { channels, loading: channelsLoading, saving: channelsSaving, saveChannel, deleteChannel } = useOrgChannels(decodedOrgId)

    const [form, setForm] = useState<Partial<ChannelConfigData>>({})
    const [isDirty, setIsDirty] = useState(false)
    const [synced, setSynced] = useState(false)
    const [campaignOptions, setCampaignOptions] = useState<VicidialCampaignOption[]>([])
    const [loadingCampaigns, setLoadingCampaigns] = useState(false)

    // Channel modal state
    const [channelModalOpen, setChannelModalOpen] = useState(false)
    const [editingChannel, setEditingChannel] = useState<Channel | null>(null)

    // Validation hooks
    const {
        validateTwilioSms,
        validateTwilioWhatsapp,
        validateBotmakerWhatsapp,
        validateMetaWhatsapp,
    } = useChannelValidation()

    // Validation state
    const [validatingTwilioSms, setValidatingTwilioSms] = useState(false)
    const [validatingWhatsapp, setValidatingWhatsapp] = useState(false)

    // Sync form when config loads or org changes
    useEffect(() => {
        if (!loading) {
            setForm(config)
            setSynced(true)
            setIsDirty(false)
            setCampaignOptions([])
        }
    }, [config, loading])

    const getToken = useCallback(() => {
        return auth?.api_token || localStorage.getItem(STORAGE_NAMES.TOKEN)
    }, [auth?.api_token])

    const visibleCampaignOptions = useMemo(() => {
        const currentCampaignId = String(form.vicidial_campaign_id || '').trim()
        if (!currentCampaignId || campaignOptions.some((campaign) => campaign.campaign_id === currentCampaignId)) {
            return campaignOptions
        }

        return [
            {
                campaign_id: currentCampaignId,
                campaign_name: 'Current organization default',
                active: 'Y',
            },
            ...campaignOptions,
        ]
    }, [campaignOptions, form.vicidial_campaign_id])

    const handleChange = (field: keyof ChannelConfigData, value: any) => {
        setForm((prev) => ({ ...prev, [field]: value }))
        setIsDirty(true)
    }

    const handleOrgChange = (orgId: string) => {
        setSelectedOrgId(orgId)
        setSynced(false)
        setIsDirty(false)
        setCampaignOptions([])
    }

    const handleLoadCampaigns = async () => {
        const token = getToken()
        if (!token) {
            toast.error('You must be logged in to load PolicyDial campaigns.')
            return
        }

        const baseUrl = String(form.vicidial_base_url || '').trim()
        const apiUser = String(form.vicidial_api_user || '').trim()
        const apiPassword = String(form.vicidial_api_password || '').trim()

        if (!baseUrl || !apiUser || !apiPassword) {
            toast.error('Base URL, API User and API Password are required to load PolicyDial campaigns.')
            return
        }

        setLoadingCampaigns(true)
        try {
            const campaigns = await fetchVicidialCampaignOptions({
                token,
                baseUrl,
                apiUser,
                apiPassword,
            })
            setCampaignOptions(campaigns)

            if (!campaigns.length) {
                toast.info('No PolicyDial campaigns were returned for this organization.')
                return
            }

            toast.success('PolicyDial campaigns loaded successfully.')
        } catch (error: any) {
            const message = error?.response?.data?.error || 'Unable to load PolicyDial campaigns.'
            toast.error(message)
            setCampaignOptions([])
        } finally {
            setLoadingCampaigns(false)
        }
    }

    const handleSave = async () => {
        const result = await saveOrgConfig(form)
        if (result.ok) {
            toast.success('Channel configuration saved successfully')
            setIsDirty(false)
        } else {
            toast.error(`Error saving channel configuration: ${result.error}`, { autoClose: 8000 })
        }
    }

    const handleAddChannel = () => {
        setEditingChannel(null)
        setChannelModalOpen(true)
    }

    const handleEditChannel = (channel: Channel) => {
        setEditingChannel(channel)
        setChannelModalOpen(true)
    }

    const handleSaveChannel = async (data: Partial<Channel>) => {
        const saved = await saveChannel(data)
        if (saved) {
            toast.success(data.id ? 'Channel updated' : 'Channel created')
            setChannelModalOpen(false)
            setEditingChannel(null)
        } else {
            toast.error('Error saving channel')
        }
        return saved as any
    }

    const handleDeleteChannel = async (channelId: number) => {
        if (!window.confirm('Are you sure you want to delete this channel?')) return
        const success = await deleteChannel(channelId)
        if (success) {
            toast.success('Channel deleted')
        } else {
            toast.error('Error deleting channel')
        }
    }

    const handleValidateTwilioSms = async () => {
        const accountSid = form.twilio_account_sid?.trim()
        const authToken = form.twilio_auth_token?.trim()

        if (!accountSid || !authToken) {
            toast.warning('Please fill in Account SID and Auth Token')
            return
        }

        setValidatingTwilioSms(true)
        try {
            const result = await validateTwilioSms({
                variables: { accountSid, authToken },
            })
            if (result.data?.validateTwilioSms?.payload?.valid) {
                toast.success(result.data.validateTwilioSms.payload.message)
            } else {
                toast.error(result.data?.validateTwilioSms?.payload?.message || 'Validation failed')
            }
        } catch (error: any) {
            toast.error(error.message || 'Validation error')
        } finally {
            setValidatingTwilioSms(false)
        }
    }

    const handleValidateWhatsapp = async () => {
        const provider = form.whatsapp_provider?.trim()

        if (!provider || provider === 'NONE') {
            toast.warning('Please select a WhatsApp provider')
            return
        }

        setValidatingWhatsapp(true)
        try {
            let result
            if (provider === 'TWILIO') {
                const accountSid = form.twilio_whatsapp_account_sid?.trim() || form.twilio_account_sid?.trim()
                const authToken = form.twilio_whatsapp_auth_token?.trim() || form.twilio_auth_token?.trim()

                if (!accountSid || !authToken) {
                    toast.warning('Please fill in Twilio Account SID and Auth Token')
                    setValidatingWhatsapp(false)
                    return
                }

                result = await validateTwilioWhatsapp({
                    variables: { accountSid, authToken },
                })
            } else if (provider === 'BOTMAKER') {
                const apiToken = form.botmaker_api_token?.trim()
                const defaultChannelId = form.botmaker_default_channel_id?.trim()
                const apiUrl = form.botmaker_api_url?.trim() || undefined

                if (!apiToken || !defaultChannelId) {
                    toast.warning('Please fill in API Token and Default Channel ID')
                    setValidatingWhatsapp(false)
                    return
                }

                result = await validateBotmakerWhatsapp({
                    variables: { apiToken, defaultChannelId, apiUrl },
                })
            } else if (provider === 'META') {
                const accessToken = form.meta_whatsapp_access_token?.trim()
                const phoneNumberId = form.meta_whatsapp_phone_number_id?.trim()

                if (!accessToken || !phoneNumberId) {
                    toast.warning('Please fill in Access Token and Phone Number ID')
                    setValidatingWhatsapp(false)
                    return
                }

                result = await validateMetaWhatsapp({
                    variables: { accessToken, phoneNumberId },
                })
            }

            if (result?.data) {
                const payload = (Object.values(result.data)[0] as any)?.payload
                if (payload?.valid) {
                    toast.success(payload.message)
                } else {
                    toast.error(payload?.message || 'Validation failed')
                }
            }
        } catch (error: any) {
            toast.error(error.message || 'Validation error')
        } finally {
            setValidatingWhatsapp(false)
        }
    }

    if (loading || (isSuperAdmin && loadingOrgs)) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
                <Spinner animation="border" variant="primary" />
            </div>
        )
    }

    if (!isAdmin) {
        return (
            <div className="container-xxl py-5">
                <div className="alert alert-danger d-flex align-items-center">
                    <i className="bi bi-exclamation-triangle-fill fs-2 me-3"></i>
                    <div>
                        <h4 className="alert-heading">Permission Denied</h4>
                        <p className="mb-0">You do not have permission to configure organization channels. Please contact your administrator.</p>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="container-xxl py-5">
            <div className="d-flex justify-content-between align-items-center mb-6">
                <div>
                    <h1 className="fs-2 fw-bold text-gray-900 mb-1">Channel Configuration</h1>
                    <p className="text-muted fs-6 mb-0">
                        Configure communication channels for your organization
                    </p>
                </div>
                <div className="d-flex align-items-center gap-3">
                    {config.webhook_token && (
                        <span className="text-muted fs-7">
                            Webhook: <code className="text-primary">{config.webhook_token.slice(0, 12)}...</code>
                        </span>
                    )}
                    <Button
                        variant="primary"
                        onClick={handleSave}
                        disabled={saving || !isDirty}
                        className="px-6"
                    >
                        {saving ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Saving...
                            </>
                        ) : (
                            <>
                                <i className="bi bi-check-lg me-1"></i>
                                Save Changes
                            </>
                        )}
                    </Button>
                </div>
            </div>

            {/* Organization Selector — SuperAdmin only */}
            {isSuperAdmin && (
                <Card className="mb-5 shadow-sm border-0" style={{ background: 'linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)' }}>
                    <Card.Body className="py-4">
                        <div className="d-flex align-items-center gap-4">
                            <div className="d-flex align-items-center gap-2">
                                <i className="bi bi-building fs-3 text-primary"></i>
                                <span className="fw-bold text-gray-800 fs-5">Organization</span>
                            </div>
                            <Form.Select
                                value={selectedOrgId || ''}
                                onChange={(e) => handleOrgChange(e.target.value)}
                                className="mw-400px"
                                style={{ borderColor: '#c5d2e8' }}
                            >
                                <option value="" disabled>Select organization...</option>
                                {organizations.map((org: any) => (
                                    <option key={org.id} value={org.id}>
                                        {org.name}
                                    </option>
                                ))}
                            </Form.Select>
                            {selectedOrgId && (
                                <Badge bg="light" text="dark" className="fs-7 px-3 py-2 border">
                                    Configuring: <strong>{organizations.find((o: any) => o.id === selectedOrgId)?.name || config.organization_name}</strong>
                                </Badge>
                            )}
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* ═════ TELEPHONY SYSTEM ═════ */}
            <Card className="mb-4 shadow-sm">
                <Card.Body className="pb-3">
                    <div className="d-flex align-items-center gap-3 mb-4">
                        <i className="bi bi-telephone-forward fs-2 text-primary"></i>
                        <h4 className="fw-bold text-gray-800 mb-0">Telephony System</h4>
                    </div>

                    {/* ─── Provider Toggle ─── */}
                    <div className="d-flex align-items-center gap-3 mb-4 p-3 rounded bg-light border">
                        <div className="fw-semibold text-gray-700 flex-shrink-0">☎️ RingCentral</div>
                        <Form.Check
                            type="switch"
                            id="provider_toggle"
                            label={
                                <span className="d-inline-flex align-items-center gap-1">
                                    <img
                                        src="/media/tfi/policydial-icon.svg"
                                        alt="PolicyDial"
                                        style={{ width: 18, height: 18, objectFit: 'contain' }}
                                    />
                                    PolicyDial
                                </span>
                            }
                            checked={form.telephony_provider === 'VICIDIAL'}
                            onChange={(e) => handleChange('telephony_provider', e.target.checked ? 'VICIDIAL' : 'RINGCENTRAL')}
                            className="fw-semibold"
                        />
                    </div>

                    {/* ─── PolicyDial Configuration ─── */}
                    {form.telephony_provider === 'VICIDIAL' && (
                        <div className="border rounded p-4 bg-light">
                            <div className="d-flex align-items-center gap-2 mb-3 pb-2 border-bottom">
                                <img
                                    src="/media/tfi/policydial-webphone.svg"
                                    alt="PolicyDial"
                                    style={{ height: '32px', objectFit: 'contain' }}
                                />
                                <h5 className="fw-bold text-gray-800 mb-0">Configuration</h5>
                                <Badge bg={form.vicidial_enabled ? 'success' : 'secondary'} className="ms-auto">
                                    {form.vicidial_enabled ? 'Enabled' : 'Disabled'}
                                </Badge>
                            </div>
                            <div className="row g-3 mt-2">
                                <div className="col-12">
                                    <Form.Check
                                        type="switch"
                                        id="vicidial_enabled"
                                        label="Enable PolicyDial telephony"
                                        checked={form.vicidial_enabled || false}
                                        onChange={(e) => handleChange('vicidial_enabled', e.target.checked)}
                                        className="mb-3"
                                    />
                                </div>
                                <div className="col-md-6">
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-gray-700">Base URL</Form.Label>
                                        <Form.Control
                                            type="url"
                                            placeholder="https://vici.policytools.com"
                                            value={form.vicidial_base_url || ''}
                                            onChange={(e) => handleChange('vicidial_base_url', e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-6">
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-gray-700">Webphone URL</Form.Label>
                                        <Form.Control
                                            type="url"
                                            placeholder="https://phone.viciphone.com/v3.0/viciphone.php"
                                            value={form.vicidial_webphone_url || ''}
                                            onChange={(e) => handleChange('vicidial_webphone_url', e.target.value)}
                                        />
                                        <Form.Text className="text-muted">
                                            Used as fallback when the AGENT API cannot return the active session URL.
                                        </Form.Text>
                                    </Form.Group>
                                </div>
                                <div className="col-md-6">
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-gray-700">API User</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="6666"
                                            value={form.vicidial_api_user || ''}
                                            onChange={(e) => handleChange('vicidial_api_user', e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-6">
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-gray-700">API Password</Form.Label>
                                        <Form.Control
                                            type="text"
                                            autoComplete="off"
                                            placeholder="Enter API password"
                                            value={form.vicidial_api_password || ''}
                                            onChange={(e) => handleChange('vicidial_api_password', e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-4">
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-gray-700">Default agent user</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="support01"
                                            value={form.vicidial_agent_user || ''}
                                            onChange={(e) => handleChange('vicidial_agent_user', e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-4">
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-gray-700">Default phone code</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="1"
                                            value={form.vicidial_phone_code || ''}
                                            onChange={(e) => handleChange('vicidial_phone_code', e.target.value)}
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-4">
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-gray-700">Campaign</Form.Label>
                                        {visibleCampaignOptions.length > 0 ? (
                                            <Form.Select
                                                value={form.vicidial_campaign_id || ''}
                                                onChange={(e) => handleChange('vicidial_campaign_id', e.target.value)}
                                            >
                                                <option value=''>Select organization default campaign...</option>
                                                {visibleCampaignOptions.map((campaign) => (
                                                    <option key={campaign.campaign_id} value={campaign.campaign_id}>
                                                        {campaign.campaign_id} - {campaign.campaign_name}
                                                        {campaign.active === 'Y' ? '' : ' (Inactive)'}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        ) : (
                                            <Form.Control
                                                type="text"
                                                placeholder="0001"
                                                value={form.vicidial_campaign_id || ''}
                                                onChange={(e) => handleChange('vicidial_campaign_id', e.target.value)}
                                            />
                                        )}
                                        <div className="d-flex align-items-center gap-2 mt-2 flex-wrap">
                                            <Button
                                                variant="light-primary"
                                                size="sm"
                                                onClick={handleLoadCampaigns}
                                                disabled={loadingCampaigns}
                                            >
                                                {loadingCampaigns ? (
                                                    <>
                                                        <Spinner animation="border" size="sm" className="me-2" />
                                                        Loading...
                                                    </>
                                                ) : visibleCampaignOptions.length > 0 ? (
                                                    'Refresh campaigns'
                                                ) : (
                                                    'Load campaigns'
                                                )}
                                            </Button>
                                            <Form.Text className="text-muted mb-0">
                                                Super admins can set the organization default here. Users can still choose their active PolicyDial campaign from <code>Integrations</code>.
                                            </Form.Text>
                                        </div>
                                    </Form.Group>
                                </div>
                                <div className="col-md-4">
                                    <Form.Group>
                                        <Form.Label className="fw-semibold text-gray-700">Auto dial delay (seconds)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            min={0}
                                            step={1}
                                            placeholder="5"
                                            value={form.vicidial_dial_queue_delay_seconds ?? 5}
                                            onChange={(e) =>
                                                handleChange(
                                                    'vicidial_dial_queue_delay_seconds',
                                                    e.target.value === '' ? 5 : Math.max(0, Number(e.target.value))
                                                )
                                            }
                                        />
                                        <Form.Text className="text-muted">
                                            Wait time between saving the disposition and placing the next PolicyDial queue call.
                                        </Form.Text>
                                    </Form.Group>
                                </div>
                            </div>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* ═════ MESSAGING & CHANNELS ═════ */}
            <Card className="mb-4 shadow-sm">
                <Card.Body className="pb-3">
                    <div className="d-flex align-items-center gap-3 mb-4">
                        <i className="bi bi-chat-left-text fs-2 text-info"></i>
                        <h4 className="fw-bold text-gray-800 mb-0">Messaging & Channels</h4>
                    </div>

                    {/* ─── Twilio SMS ─── */}
                    <div className="border-bottom pb-3 mb-4">
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <i className="bi bi-chat-dots fs-4 text-info"></i>
                            <h5 className="fw-bold text-gray-800 mb-0">Twilio SMS</h5>
                            <Form.Check
                                type="switch"
                                id="twilio_enabled"
                                checked={form.twilio_enabled || false}
                                onChange={(e) => handleChange('twilio_enabled', e.target.checked)}
                                className="ms-2 mb-0"
                            />
                            <Badge bg={form.twilio_enabled ? 'success' : 'secondary'} className="ms-2">
                                {form.twilio_enabled ? 'Enabled' : 'Disabled'}
                            </Badge>
                        </div>

                        {form.twilio_enabled && (
                            <>
                                <div className="row g-1 mt-2 align-items-end">
                                    <div className="col-md-4">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">Account SID</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="AC..."
                                                value={form.twilio_account_sid || ''}
                                                onChange={(e) => handleChange('twilio_account_sid', e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-4">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">Auth Token</Form.Label>
                                            <Form.Control
                                                type="text"
                                                autoComplete="off"
                                                placeholder="Token"
                                                value={form.twilio_auth_token || ''}
                                                onChange={(e) => handleChange('twilio_auth_token', e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-3">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">Phone</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="+1858..."
                                                value={form.twilio_phone_number || ''}
                                                onChange={(e) => handleChange('twilio_phone_number', e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-1 d-flex justify-content-center">
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={handleValidateTwilioSms}
                                            disabled={validatingTwilioSms}
                                            className="w-100"
                                        >
                                            {validatingTwilioSms ? (
                                                <Spinner animation="border" size="sm" />
                                            ) : (
                                                <i className="bi bi-beaker"></i>
                                            )}
                                        </Button>
                                    </div>
                                </div>
                                {config?.webhook_token && (
                                    <>
                                        <div className="row g-2 mt-2 align-items-center p-2 bg-light border rounded">
                                            <div className="col-auto">
                                                <Form.Label className="fw-semibold text-gray-800 fs-8 mb-0">Inbound:</Form.Label>
                                            </div>
                                            <div className="col">
                                                <code className="text-break text-dark fs-7 d-block">{`webhooks/twilio/sms/${config.webhook_token}/`}</code>
                                            </div>
                                            <div className="col-auto">
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(`https://app.policytools.com/webhooks/twilio/sms/${config.webhook_token}/`)
                                                        toast.success('Webhook URL copied')
                                                    }}
                                                    className="p-1"
                                                    title="Copy webhook URL"
                                                >
                                                    <i className="bi bi-files"></i>
                                                </Button>
                                            </div>
                                        </div>
                                        <div className="row g-2 mt-2 align-items-center p-2 bg-warning border rounded">
                                            <div className="col-auto">
                                                <Form.Label className="fw-semibold text-gray-800 fs-8 mb-0">Status:</Form.Label>
                                            </div>
                                            <div className="col">
                                                <code className="text-break text-dark fs-7 d-block">webhooks/twilio/status/</code>
                                            </div>
                                            <div className="col-auto">
                                                <Button
                                                    variant="warning"
                                                    size="sm"
                                                    onClick={() => {
                                                        navigator.clipboard.writeText('https://app.policytools.com/webhooks/twilio/status/')
                                                        toast.success('Status callback URL copied')
                                                    }}
                                                    className="p-1"
                                                    title="Copy status callback URL"
                                                >
                                                    <i className="bi bi-files"></i>
                                                </Button>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </>
                        )}
                    </div>

                    {/* ─── WhatsApp Integration (Multi-Provider) ─── */}
                    <div className="border-bottom pb-3 mb-4">
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <i className="bi bi-whatsapp fs-4 text-success"></i>
                            <h5 className="fw-bold text-gray-800 mb-0">WhatsApp</h5>
                            <Form.Select
                                value={form.whatsapp_provider || 'NONE'}
                                onChange={(e) => handleChange('whatsapp_provider', e.target.value)}
                                className="mw-250px ms-2"
                                size="sm"
                            >
                                <option value="NONE">Disabled</option>
                                <option value="BOTMAKER">Botmaker</option>
                                <option value="TWILIO">Twilio</option>
                                <option value="META">Meta Cloud API</option>
                            </Form.Select>
                            <Badge bg={form.whatsapp_provider !== 'NONE' ? 'success' : 'secondary'} className="ms-2">
                                {form.whatsapp_provider !== 'NONE' ? form.whatsapp_provider : 'Disabled'}
                            </Badge>
                        </div>

                        {/* Botmaker WhatsApp Config */}
                        {form.whatsapp_provider === 'BOTMAKER' && (
                            <>
                                <div className="row g-1 animate__animated animate__fadeIn border rounded p-2 bg-light mt-2 align-items-end">
                                    <div className="col-md-4">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">API Token</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                autoComplete="off"
                                                rows={1}
                                                placeholder="JWT"
                                                value={form.botmaker_api_token || ''}
                                                onChange={(e) => handleChange('botmaker_api_token', e.target.value)}
                                                style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-3">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">Channel ID</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="ID"
                                                value={form.botmaker_default_channel_id || ''}
                                                onChange={(e) => handleChange('botmaker_default_channel_id', e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-4">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">Phone Line</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="18586096799"
                                                value={form.botmaker_phone_line_number || ''}
                                                onChange={(e) => handleChange('botmaker_phone_line_number', e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-1 d-flex align-items-end justify-content-center pb-1">
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={handleValidateWhatsapp}
                                            disabled={validatingWhatsapp}
                                            className="p-1"
                                            style={{ minWidth: '28px' }}
                                        >
                                            {validatingWhatsapp ? <Spinner animation="border" size="sm" style={{ width: '12px', height: '12px' }} /> : <i className="bi bi-beaker" style={{ fontSize: '0.75rem' }}></i>}
                                        </Button>
                                    </div>
                                </div>
                                {config?.webhook_token && (
                                    <div className="row g-1 mt-2 align-items-center">
                                        <div className="col-auto">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 mb-0">Webhook:</Form.Label>
                                        </div>
                                        <div className="col-auto flex-grow-1">
                                            <code className="text-break text-muted fs-8">{`webhooks/botmaker/${config.webhook_token}/`}</code>
                                        </div>
                                        <div className="col-auto">
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={() => {
                                                    navigator.clipboard.writeText(`https://app.policytools.com/webhooks/botmaker/${config.webhook_token}/`)
                                                    toast.success('Webhook URL copied')
                                                }}
                                                className="p-1"
                                                style={{ minWidth: '28px' }}
                                            >
                                                <i className="bi bi-files" style={{ fontSize: '0.75rem' }}></i>
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}

                        {/* Twilio WhatsApp Config */}
                        {form.whatsapp_provider === 'TWILIO' && (
                            <>
                                <div className="row g-1 animate__animated animate__fadeIn border rounded p-2 bg-light mt-2 align-items-end">
                                    <div className="col-md-4">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">Account SID</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="AC..."
                                                value={form.twilio_whatsapp_account_sid || ''}
                                                onChange={(e) => handleChange('twilio_whatsapp_account_sid', e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-3">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">Auth Token</Form.Label>
                                            <Form.Control
                                                type="text"
                                                autoComplete="off"
                                                placeholder="Token"
                                                value={form.twilio_whatsapp_auth_token || ''}
                                                onChange={(e) => handleChange('twilio_whatsapp_auth_token', e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-4">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">WhatsApp #</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="+1858"
                                                value={form.twilio_whatsapp_number || ''}
                                                onChange={(e) => handleChange('twilio_whatsapp_number', e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-1 d-flex align-items-end justify-content-center pb-1">
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={handleValidateWhatsapp}
                                            disabled={validatingWhatsapp}
                                            className="p-1"
                                            style={{ minWidth: '28px' }}
                                        >
                                            {validatingWhatsapp ? <Spinner animation="border" size="sm" style={{ width: '12px', height: '12px' }} /> : <i className="bi bi-beaker" style={{ fontSize: '0.75rem' }}></i>}
                                        </Button>
                                    </div>
                                </div>
                                {config?.webhook_token && (
                                    <>
                                        <div className="row g-2 mt-2 align-items-center p-2 bg-light border rounded">
                                            <div className="col-auto">
                                                <Form.Label className="fw-semibold text-gray-800 fs-8 mb-0">Inbound:</Form.Label>
                                            </div>
                                            <div className="col">
                                                <code className="text-break text-dark fs-7 d-block">{`webhooks/twilio/whatsapp/${config.webhook_token}/`}</code>
                                            </div>
                                            <div className="col-auto">
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(`https://app.policytools.com/webhooks/twilio/whatsapp/${config.webhook_token}/`)
                                                        toast.success('Webhook URL copied')
                                                    }}
                                                    className="p-1"
                                                    title="Copy webhook URL"
                                                >
                                                    <i className="bi bi-files"></i>
                                                </Button>
                                            </div>
                                        </div>
                                        <div className="row g-2 mt-2 align-items-center p-2 bg-warning border rounded">
                                            <div className="col-auto">
                                                <Form.Label className="fw-semibold text-gray-800 fs-8 mb-0">Status:</Form.Label>
                                            </div>
                                            <div className="col">
                                                <code className="text-break text-dark fs-7 d-block">webhooks/twilio/status/</code>
                                            </div>
                                            <div className="col-auto">
                                                <Button
                                                    variant="warning"
                                                    size="sm"
                                                    onClick={() => {
                                                        navigator.clipboard.writeText('https://app.policytools.com/webhooks/twilio/status/')
                                                        toast.success('Status callback URL copied')
                                                    }}
                                                    className="p-1"
                                                    title="Copy status callback URL"
                                                >
                                                    <i className="bi bi-files"></i>
                                                </Button>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </>
                        )}

                        {/* Meta WhatsApp Config */}
                        {form.whatsapp_provider === 'META' && (
                            <>
                                <div className="row g-1 animate__animated animate__fadeIn border rounded p-2 bg-light mt-2 align-items-end">
                                    <div className="col-md-4">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">Access Token</Form.Label>
                                            <Form.Control
                                                type="password"
                                                placeholder="EAAL..."
                                                value={form.meta_whatsapp_access_token || ''}
                                                onChange={(e) => handleChange('meta_whatsapp_access_token', e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-3">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">Phone ID</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="ID"
                                                value={form.meta_whatsapp_phone_number_id || ''}
                                                onChange={(e) => handleChange('meta_whatsapp_phone_number_id', e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-4">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">Verify Token</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Token"
                                                value={form.meta_whatsapp_verify_token || ''}
                                                onChange={(e) => handleChange('meta_whatsapp_verify_token', e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-1 d-flex align-items-end justify-content-center pb-1">
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={handleValidateWhatsapp}
                                            disabled={validatingWhatsapp}
                                            className="p-1"
                                            style={{ minWidth: '28px' }}
                                        >
                                            {validatingWhatsapp ? <Spinner animation="border" size="sm" style={{ width: '12px', height: '12px' }} /> : <i className="bi bi-beaker" style={{ fontSize: '0.75rem' }}></i>}
                                        </Button>
                                    </div>
                                </div>
                                {config?.webhook_token && (
                                    <div className="border-top mt-2 pt-2">
                                        <div className="row g-2 align-items-center mb-2 p-2 bg-light border rounded">
                                            <div className="col-auto">
                                                <Form.Label className="fw-semibold text-gray-800 fs-8 mb-0">Webhook:</Form.Label>
                                            </div>
                                            <div className="col">
                                                <code className="text-break text-dark fs-7 d-block">{`webhooks/meta/${config.webhook_token}/`}</code>
                                            </div>
                                            <div className="col-auto">
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(`https://app.policytools.com/webhooks/meta/${config.webhook_token}/`)
                                                        toast.success('Webhook URL copied')
                                                    }}
                                                    className="p-1"
                                                    title="Copy webhook URL"
                                                >
                                                    <i className="bi bi-files"></i>
                                                </Button>
                                            </div>
                                        </div>
                                        <div className="row g-2 align-items-center p-2 bg-light border rounded">
                                            <div className="col-auto">
                                                <Form.Label className="fw-semibold text-gray-800 fs-8 mb-0">Verify Token:</Form.Label>
                                            </div>
                                            <div className="col">
                                                <code className="text-dark fs-7 d-block">{form.meta_whatsapp_verify_token || '—'}</code>
                                            </div>
                                            <div className="col-auto">
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    onClick={() => {
                                                        navigator.clipboard.writeText(form.meta_whatsapp_verify_token || '')
                                                        toast.success('Verify token copied')
                                                    }}
                                                    disabled={!form.meta_whatsapp_verify_token}
                                                    className="p-1"
                                                    title="Copy verify token"
                                                >
                                                    <i className="bi bi-files"></i>
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </Card.Body>
            </Card>

            {/* ═════ EMAIL SYSTEM ═════ */}
            <Card className="mb-4 shadow-sm">
                <Card.Body className="pb-3">
                    <div className="d-flex align-items-center gap-3 mb-4">
                        <i className="bi bi-envelope fs-2 text-warning"></i>
                        <h4 className="fw-bold text-gray-800 mb-0">Email System</h4>
                    </div>
                    {/* ─── Email SMTP ─── */}
                    <div className="border-bottom pb-3 mb-4">
                        <div className="d-flex align-items-center gap-2 mb-3">
                            {/* <i className="bi bi-envelope fs-4 text-warning"></i> */}
                            <h5 className="fw-bold text-gray-800 mb-0">Email SMTP</h5>
                            <Form.Check
                                type="switch"
                                id="email_enabled"
                                checked={form.email_enabled || false}
                                onChange={(e) => handleChange('email_enabled', e.target.checked)}
                                className="ms-2 mb-0"
                            />
                            <Badge bg={form.email_enabled ? 'success' : 'secondary'} className="ms-2">
                                {form.email_enabled ? 'Enabled' : 'Disabled'}
                            </Badge>
                        </div>

                        {form.email_enabled && (
                            <>
                                <div className="row g-1 mt-2 align-items-end">
                                    <div className="col-md-4">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">SMTP Host</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="smtp.gmail.com"
                                                value={form.email_host || ''}
                                                onChange={(e) => handleChange('email_host', e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-2">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">Port</Form.Label>
                                            <Form.Control
                                                type="number"
                                                placeholder="587"
                                                value={form.email_port || 587}
                                                onChange={(e) => handleChange('email_port', parseInt(e.target.value) || 587)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-2">
                                        <Form.Group className="mb-0 d-flex align-items-center">
                                            <Form.Check
                                                type="switch"
                                                id="email_use_tls"
                                                label="TLS"
                                                checked={form.email_use_tls !== false}
                                                onChange={(e) => handleChange('email_use_tls', e.target.checked)}
                                                className="mb-0 fs-7"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-4">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">User</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="user@example.com"
                                                value={form.email_host_user || ''}
                                                onChange={(e) => handleChange('email_host_user', e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                </div>
                                <div className="row g-1 mt-1 align-items-end">
                                    <div className="col-md-4">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">Password</Form.Label>
                                            <Form.Control
                                                type="password"
                                                autoComplete="off"
                                                placeholder="••••••••"
                                                value={form.email_host_password || ''}
                                                onChange={(e) => handleChange('email_host_password', e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-4">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">From</Form.Label>
                                            <Form.Control
                                                type="email"
                                                placeholder="noti@example.com"
                                                value={form.email_from || ''}
                                                onChange={(e) => handleChange('email_from', e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-4">
                                        <Form.Group className="mb-0">
                                            <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">Reply-To</Form.Label>
                                            <Form.Control
                                                type="email"
                                                placeholder="support@example.com"
                                                value={form.email_reply_to || ''}
                                                onChange={(e) => handleChange('email_reply_to', e.target.value)}
                                                size="sm"
                                            />
                                        </Form.Group>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </Card.Body>
            </Card>

            {/* ═════ INTEGRATIONS ═════ */}
            <Card className="mb-4 shadow-sm">
                <Card.Body className="pb-3">
                    <div className="d-flex align-items-center gap-3 mb-4">
                        <i className="bi bi-link-45deg fs-2 text-success"></i>
                        <h4 className="fw-bold text-gray-800 mb-0">Integrations</h4>
                    </div>
                    {/* ─── HealthSherpa Medicare ─── */}
                    <div className="border-bottom pb-3 mb-4">
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <i className="bi bi-heart-pulse fs-4 text-danger"></i>
                            <h5 className="fw-bold text-gray-800 mb-0">HealthSherpa Medicare</h5>
                            <Form.Check
                                type="switch"
                                id="healthsherpa_enabled"
                                checked={form.healthsherpa_enabled || false}
                                onChange={(e) => handleChange('healthsherpa_enabled', e.target.checked)}
                                className="ms-2 mb-0"
                            />
                            <Badge bg={form.healthsherpa_enabled ? 'success' : 'secondary'} className="ms-2">
                                {form.healthsherpa_enabled ? 'Enabled' : 'Disabled'}
                            </Badge>
                        </div>

                        {form.healthsherpa_enabled && (
                            <div className="row g-1 mt-2 align-items-end">
                                <div className="col-md-4">
                                    <Form.Group className="mb-0">
                                        <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">Agent Email</Form.Label>
                                        <Form.Control
                                            type="email"
                                            placeholder="agent@example.com"
                                            value={form.healthsherpa_email || ''}
                                            onChange={(e) => handleChange('healthsherpa_email', e.target.value)}
                                            size="sm"
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-3">
                                    <Form.Group className="mb-0">
                                        <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">API Key</Form.Label>
                                        <Form.Control
                                            type="password"
                                            autoComplete="off"
                                            placeholder="Key"
                                            value={form.healthsherpa_api_key || ''}
                                            onChange={(e) => handleChange('healthsherpa_api_key', e.target.value)}
                                            size="sm"
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-5">
                                    <Form.Group className="mb-0">
                                        <Form.Label className="fw-semibold text-gray-700 fs-8 d-block mb-1">Webhook Secret</Form.Label>
                                        <Form.Control
                                            type="password"
                                            autoComplete="off"
                                            placeholder="Secret"
                                            value={form.healthsherpa_webhook_secret || ''}
                                            onChange={(e) => handleChange('healthsherpa_webhook_secret', e.target.value)}
                                            size="sm"
                                        />
                                    </Form.Group>
                                </div>
                            </div>
                        )}
                    </div>
                </Card.Body>
            </Card>

            {/* Channel Form Modal */}
            {decodedOrgId && (
                <ChannelFormModal
                    isOpen={channelModalOpen}
                    onClose={() => {
                        setChannelModalOpen(false)
                        setEditingChannel(null)
                    }}
                    onSave={handleSaveChannel}
                    saving={channelsSaving}
                    initialData={editingChannel}
                    groupMembers={[]} // Empty for org-level channels
                />
            )}
        </div>
    )
}
